import {
  dec,
  formatQuantity,
  MOVEMENT_KINDS,
  MOVEMENT_LABELS,
  plural,
  type UnitId,
} from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Columns3, Filter, Undo2 } from "lucide-react";
import type { ReactNode } from "react";
import { type Movement, send, useChange, useItems, useMovementLog } from "../api";
import {
  Empty,
  ErrorNote,
  Menu,
  MenuDivider,
  MenuHeading,
  MenuItem,
  Toolbar,
  usePageTitle,
} from "../components/ui";
import { movementLabel, showDateTime, unitLabel } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { MOVEMENT_COLUMNS, type MovementColumnId } from "../layout/layout";
import { useNotify } from "../notify";

function UndoButton({ movement }: { movement: Movement }) {
  const notify = useNotify();
  const change = useChange((_: undefined, key) => send.reverse({ id: movement.id }, key), {
    onSuccess: () =>
      notify({
        level: "notice",
        title: "Movement undone",
        body: "The log keeps both entries: the original and the one that reversed it.",
      }),
  });
  // Two items made one stay one: the other's lines and pulls moved with its stock.
  if (movement.reversesId || movement.reversedById || movement.kind === "merge") return null;
  return (
    <>
      <button
        type="button"
        className="link-button row"
        style={{ gap: 4 }}
        disabled={change.isPending}
        title="Record the opposite of this entry"
        onClick={() => change.mutate(undefined)}
      >
        <Undo2 size={14} /> Undo
      </button>
      <ErrorNote error={change.error} />
    </>
  );
}

/** The movement log as a table. Used on the History page and on each item page. */
export function MovementTable({
  movements,
  hideItem,
}: {
  movements: Movement[];
  hideItem?: boolean;
}) {
  const { layout } = useLayout();
  const { data: items = [] } = useItems();
  const units = new Map(items.map((i) => [i.id, i.displayUnit]));
  const names = new Map(items.map((i) => [i.id, i.displayName]));
  const columns = layout.movements.columns.filter(
    (c) => c.visible && !(hideItem && c.id === "item"),
  );
  const label = (id: MovementColumnId) => MOVEMENT_COLUMNS.find((c) => c.id === id)?.label ?? id;
  const cell: Record<MovementColumnId, (m: Movement) => ReactNode> = {
    when: (m) => showDateTime(m.occurredAt),
    kind: (m) => movementLabel(m),
    item: (m) => (
      <Link to="/items/$itemId" params={{ itemId: m.itemId }}>
        {names.get(m.itemId) ?? m.itemName}
      </Link>
    ),
    quantity: (m) => {
      const unit = (units.get(m.itemId) ?? "g") as UnitId;
      const text = formatQuantity(m.quantity, { unit });
      return (
        <span className={`num ${m.quantity.startsWith("-") ? "" : "positive"}`}>
          {m.quantity.startsWith("-") ? text : `+${text}`}
        </span>
      );
    },
    location: (m) => m.locationName,
    lot: (m) => m.lotCode,
    entered: (m) => {
      if (!m.enteredValue) return "";
      // As entered, to three places at most: an import's converted grams carried seven (the critique,
      // 2026-10-06), which no one entered.
      const value = dec(m.enteredValue).round(3).toString();
      return `${value} ${
        m.enteredUnit === "pack"
          ? plural(m.packName ?? "pack", value)
          : unitLabel(m.enteredUnit ?? "")
      }`;
    },
    counterparty: (m) => m.counterparty ?? "",
    note: (m) => <span className="muted">{m.note ?? ""}</span>,
  };
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.id} className={c.id === "quantity" ? "num" : undefined}>
                {label(c.id)}
              </th>
            ))}
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {movements.map((m) => (
            <tr key={m.id} className={m.reversedById ? "reversed" : undefined}>
              {columns.map((c) => (
                <td key={c.id} className={c.id === "quantity" ? "num" : undefined}>
                  {cell[c.id](m)}
                </td>
              ))}
              <td className="actions">
                <UndoButton movement={m} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MovementsPage() {
  usePageTitle("History");
  const { layout, update } = useLayout();
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useMovementLog(
    layout.movements.kind,
  );
  const kind = layout.movements.kind;
  const movements = (data?.pages ?? []).flatMap((p) => p.movements);
  const setMov = (patch: Partial<typeof layout.movements>) =>
    update((l) => ({ ...l, movements: { ...l.movements, ...patch } }));

  return (
    <>
      <Toolbar
        left={
          <Link to="/settings" className="toolbar-button" title="Back to settings">
            <ArrowLeft size={20} />
            <span className="label">Settings</span>
          </Link>
        }
        right={
          <>
            <Menu icon={<Filter size={20} />} label="Filter" badge={kind !== null}>
              {() => (
                <>
                  <MenuHeading>Show</MenuHeading>
                  <MenuItem checked={kind === null} onClick={() => setMov({ kind: null })}>
                    Everything
                  </MenuItem>
                  <MenuDivider />
                  {MOVEMENT_KINDS.map((k) => (
                    <MenuItem key={k} checked={kind === k} onClick={() => setMov({ kind: k })}>
                      {MOVEMENT_LABELS[k]}
                    </MenuItem>
                  ))}
                </>
              )}
            </Menu>
            <Menu icon={<Columns3 size={20} />} label="Columns">
              {() =>
                layout.movements.columns.map((c) => {
                  const def = MOVEMENT_COLUMNS.find((d) => d.id === c.id);
                  const required = def && "required" in def && def.required;
                  return (
                    <MenuItem
                      key={c.id}
                      checked={c.visible}
                      disabled={Boolean(required)}
                      onClick={() =>
                        setMov({
                          columns: layout.movements.columns.map((x) =>
                            x.id === c.id ? { ...x, visible: !x.visible } : x,
                          ),
                        })
                      }
                    >
                      {def?.label}
                    </MenuItem>
                  );
                })
              }
            </Menu>
          </>
        }
      />
      <div className="page-body">
        <h1 className="page-title">History</h1>
        <p className="muted" style={{ marginTop: -8 }}>
          Every change to stock, newest first. Nothing here is ever edited: an undo is a new entry
          that cancels the old one.
        </p>
        <ErrorNote error={error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        {/* Only an answer says the log is empty: a first page that failed says nothing of it. */}
        {data && movements.length === 0 ? (
          <Empty title={kind ? "Nothing of this kind recorded yet" : "Nothing recorded yet"} />
        ) : null}
        {movements.length > 0 ? <MovementTable movements={movements} /> : null}
        {hasNextPage ? (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              className="button"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              Show more
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
